import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toISODateSafe } from '../../../utils/dateUtils';
import {
  Crown,
  Shield,
  User,
  Check,
  X,
  Filter,
  Download,
  Search,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import type { EmpresaListItem, UsuarioEmpresa } from '../../../types';

interface PermissionsMatrixProps {
  empresa: EmpresaListItem;
  usuarios: UsuarioEmpresa[];
  loading: boolean;
}

const PermissionsMatrix: React.FC<PermissionsMatrixProps> = ({
  empresa,
  usuarios,
  loading
}) => {
  const { t } = useTranslation();
  const [filtroRol, setFiltroRol] = useState<string>('todos');
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [mostrarSoloActivos, setMostrarSoloActivos] = useState(true);

  const permisos = [
    { key: 'puede_crear_reportes', label: 'Crear Reportes', desc: 'Crear nuevos reportes' },
    { key: 'puede_editar_reportes_propios', label: 'Editar Propios', desc: 'Editar reportes propios' },
    { key: 'puede_ver_reportes_otros', label: 'Ver Otros', desc: 'Ver reportes de otros' },
    { key: 'puede_editar_reportes_otros', label: 'Editar Otros', desc: 'Editar reportes de otros' },
    { key: 'puede_aprobar_reportes', label: 'Aprobar', desc: 'Aprobar reportes' },
    { key: 'puede_gestionar_referencias', label: 'Gestionar Ref.', desc: 'Gestionar referencias' },
    { key: 'puede_gestionar_usuarios', label: 'Gestionar Usr.', desc: 'Gestionar usuarios' }
  ];

  // Filtrar usuarios
  const usuariosFiltrados = usuarios.filter(usuario => {
    const coincideBusqueda = !busquedaUsuario || 
      usuario.usuario_info.nombre_completo?.toLowerCase().includes(busquedaUsuario.toLowerCase()) ||
      usuario.usuario_info.username.toLowerCase().includes(busquedaUsuario.toLowerCase()) ||
      usuario.usuario_info.email.toLowerCase().includes(busquedaUsuario.toLowerCase());
    
    const coincidenRol = filtroRol === 'todos' || usuario.rol_en_empresa === filtroRol;
    const coincidenEstado = !mostrarSoloActivos || usuario.es_activa;
    
    return coincideBusqueda && coincidenRol && coincidenEstado;
  });

  const getRolIcon = (rol: string) => {
    switch (rol) {
      case 'administrador':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'supervisor':
        return <Shield className="h-4 w-4 text-blue-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRolBadge = (rol: string) => {
    const configs = {
      administrador: 'bg-yellow-100 text-yellow-800',
      supervisor: 'bg-blue-100 text-blue-800',
      operador: 'bg-gray-100 text-gray-800'
    };
    
    return configs[rol as keyof typeof configs] || configs.operador;
  };

  const getPermisosStats = () => {
    if (usuariosFiltrados.length === 0) return { total: 0, promedio: 0 };
    
    const totalPermisos = usuariosFiltrados.reduce((acc, usuario) => {
      return acc + Object.values(usuario.permisos).filter(Boolean).length;
    }, 0);
    
    const promedio = totalPermisos / usuariosFiltrados.length;
    const maxPermisos = permisos.length * usuariosFiltrados.length;
    
    return {
      total: totalPermisos,
      maximo: maxPermisos,
      promedio: Math.round(promedio * 10) / 10,
      porcentaje: Math.round((totalPermisos / maxPermisos) * 100)
    };
  };

  const exportarMatrix = () => {
    // Crear CSV con la matriz de permisos
    const headers = ['Usuario', 'Email', 'Rol', 'Estado', ...permisos.map(p => p.label)];
    const rows = usuariosFiltrados.map(usuario => [
      usuario.usuario_info.nombre_completo || usuario.usuario_info.username,
      usuario.usuario_info.email,
      usuario.rol_en_empresa,
      usuario.es_activa ? t('common.active') : t('common.inactive'),
      ...permisos.map(p => usuario.permisos[p.key as keyof typeof usuario.permisos] ? t('common.yes') : t('common.no'))
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `permisos-${empresa.nombre.replace(/\s+/g, '-')}-${toISODateSafe(new Date())}.csv`;
    link.click();
  };

  const stats = getPermisosStats();

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header con filtros y stats */}
      <div className="p-6 border-b border-gray-200 space-y-4">
        {/* Estadísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-green-900">{usuariosFiltrados.length}</p>
            <p className="text-xs text-green-600">Usuarios</p>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-blue-900">{stats.total}</p>
            <p className="text-xs text-blue-600">Permisos totales</p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-purple-900">{stats.promedio}</p>
            <p className="text-xs text-purple-600">Promedio por usuario</p>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-orange-900">{stats.porcentaje}%</p>
            <p className="text-xs text-orange-600">Cobertura</p>
          </div>

        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              value={busquedaUsuario}
              onChange={(e) => setBusquedaUsuario(e.target.value)}
              placeholder={`${t('common.search')} usuario...`}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filtroRol}
              onChange={(e) => setFiltroRol(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="todos">{t('common.all')} los roles</option>
              <option value="administrador">Administradores</option>
              <option value="supervisor">Supervisores</option>
              <option value="operador">Operadores</option>
            </select>
          </div>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={mostrarSoloActivos}
              onChange={(e) => setMostrarSoloActivos(e.target.checked)}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Solo {t('common.active').toLowerCase()}s</span>
          </label>

          <div className="ml-auto">
            <button
              onClick={exportarMatrix}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>{t('common.export')} CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Matriz de permisos */}
      <div className="flex-1 overflow-auto">
        {usuariosFiltrados.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('common.no_results')}
            </h3>
            <p className="text-gray-600">
              {t('common.clear_filters')}
            </p>
          </div>
        ) : (
          <div className="min-w-full">
            {/* Header fijo */}
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
              <div className="grid gap-1 p-4" style={{ 
                gridTemplateColumns: `300px repeat(${permisos.length}, minmax(120px, 1fr))`
              }}>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider p-2">
                  Usuario
                </div>
                {permisos.map(permiso => (
                  <div 
                    key={permiso.key} 
                    className="text-xs font-medium text-gray-500 uppercase tracking-wider p-2 text-center"
                    title={permiso.desc}
                  >
                    {permiso.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Filas de usuarios */}
            <div className="divide-y divide-gray-200">
              {usuariosFiltrados.map((usuario, index) => (
                <div 
                  key={usuario.usuario_id}
                  className={`grid gap-1 p-4 hover:bg-gray-50 transition-colors ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } ${
                    !usuario.es_activa ? 'opacity-60' : ''
                  }`}
                  style={{ 
                    gridTemplateColumns: `300px repeat(${permisos.length}, minmax(120px, 1fr))`
                  }}
                >
                  {/* Información del usuario */}
                  <div className="p-2 flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                        {getRolIcon(usuario.rol_en_empresa)}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {usuario.usuario_info.nombre_completo || usuario.usuario_info.username}
                        </p>
                        {usuario.es_empresa_principal && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Principal
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          getRolBadge(usuario.rol_en_empresa)
                        }`}>
                          {usuario.rol_en_empresa}
                        </span>
                        {usuario.es_activa ? (
                          <CheckCircle className="h-3 w-3 text-green-500" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {usuario.usuario_info.email}
                      </p>
                    </div>
                  </div>

                  {/* Permisos */}
                  {permisos.map(permiso => {
                    const tienePermiso = usuario.permisos[permiso.key as keyof typeof usuario.permisos];
                    
                    return (
                      <div key={permiso.key} className="p-2 flex justify-center items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          tienePermiso 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {tienePermiso ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsMatrix;
